import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppProvider } from "@/context/AppContext";
import { Toaster } from "@/components/ui/sonner";
import { Loader } from "@/components/common/Bits";
import { AppLayout } from "@/components/layout/AppLayout";

import { NAV, canAccess } from "@/lib/nav";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import POS from "@/pages/POS";
import Approvals from "@/pages/Approvals";
import Tables from "@/pages/Tables";
import TableDetail from "@/pages/TableDetail";
import Orders from "@/pages/Orders";
import Kitchen from "@/pages/Kitchen";
import Menu from "@/pages/Menu";
import Inventory from "@/pages/Inventory";
import Customers from "@/pages/Customers";
import Staff from "@/pages/Staff";
import Reservations from "@/pages/Reservations";
import Expenses from "@/pages/Expenses";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import SuperAdmin from "@/pages/SuperAdmin";
import QRMenu from "@/pages/QRMenu";
import { Button } from "@/components/ui/button";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsAndConditions from "@/pages/TermsAndConditions";
import RefundPolicy from "@/pages/RefundPolicy";
import CookiePolicy from "@/pages/CookiePolicy";
import Signup from "@/pages/Signup";
import Support from "@/pages/Support";
import SalesPartners from "@/pages/SalesPartners";

const Protected = ({ children }) => {
  const { user, checked } = useAuth();
  if (!checked || user === null)
    return (
      <div className="min-h-screen grid place-items-center">
        <Loader label="Starting StayServe" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
};
const NoAccess = () => {
  return (
    <div className="min-h-[70vh] grid place-items-center p-6">
      <div className="text-center max-w-md">
        <div className="text-5xl mb-4">🔒</div>

        <h1 className="font-display text-2xl font-bold">Access Restricted</h1>

        <p className="text-sm text-muted-foreground mt-2">
          You don't have permission to access this section. Please contact the
          restaurant owner or manager.
        </p>

        <Button
          type="button"
          className="mt-5 rounded-xl"
          onClick={() => window.history.back()}
        >
          Go Back
        </Button>
      </div>
    </div>
  );
};
function PermissionRoute({ module, children }) {
  const { user } = useAuth();

  if (!canAccess(user, module)) {
    return <Navigate to="/app/no-access" replace />;
  }

  return children;
}

function AppHomeRedirect() {
  const { user } = useAuth();

  if (user?.role === "super_admin") {
    return <Navigate to="/app/admin" replace />;
  }

  if (canAccess(user, "dashboard")) {
    return <Dashboard />;
  }

  const firstAllowed = NAV.find(
    (item) => item.module !== "dashboard" && canAccess(user, item.module),
  );

  if (firstAllowed) {
    return <Navigate to={firstAllowed.to} replace />;
  }

  return <Navigate to="/app/no-access" replace />;
}

const Shell = () => (
  <AppProvider>
    <Routes>
      <Route element={<AppLayout />}>
        <Route path="no-access" element={<NoAccess />} />
        <Route index element={<AppHomeRedirect />} />

        <Route
          path="pos"
          element={
            <PermissionRoute module="pos">
              <POS />
            </PermissionRoute>
          }
        />

        <Route
          path="approvals"
          element={
            <PermissionRoute module="approvals">
              <Approvals />
            </PermissionRoute>
          }
        />
        <Route
          path="tables"
          element={
            <PermissionRoute module="tables">
              <Tables />
            </PermissionRoute>
          }
        />

        <Route
          path="tables/:tableId"
          element={
            <PermissionRoute module="tables">
              <TableDetail />
            </PermissionRoute>
          }
        />

        <Route
          path="orders"
          element={
            <PermissionRoute module="orders">
              <Orders />
            </PermissionRoute>
          }
        />

        <Route
          path="kitchen"
          element={
            <PermissionRoute module="kitchen">
              <Kitchen />
            </PermissionRoute>
          }
        />

        <Route
          path="menu"
          element={
            <PermissionRoute module="menu">
              <Menu />
            </PermissionRoute>
          }
        />

        <Route
          path="inventory"
          element={
            <PermissionRoute module="inventory">
              <Inventory />
            </PermissionRoute>
          }
        />

        <Route
          path="customers"
          element={
            <PermissionRoute module="customers">
              <Customers />
            </PermissionRoute>
          }
        />

        <Route
          path="staff"
          element={
            <PermissionRoute module="staff">
              <Staff />
            </PermissionRoute>
          }
        />

        <Route
          path="reservations"
          element={
            <PermissionRoute module="reservations">
              <Reservations />
            </PermissionRoute>
          }
        />

        <Route
          path="expenses"
          element={
            <PermissionRoute module="expenses">
              <Expenses />
            </PermissionRoute>
          }
        />

        <Route
          path="reports"
          element={
            <PermissionRoute module="reports">
              <Reports />
            </PermissionRoute>
          }
        />
        <Route
          path="support"
          element={
            <PermissionRoute module="support">
              <Support />
            </PermissionRoute>
          }
        />

        <Route
          path="settings"
          element={
            <PermissionRoute module="settings">
              <Settings />
            </PermissionRoute>
          }
        />

        <Route
          path="admin"
          element={
            <PermissionRoute module="super_admin">
              <SuperAdmin />
            </PermissionRoute>
          }
        />
        <Route
          path="admin/sales-partners"
          element={
            <PermissionRoute module="sales_partners">
              <SalesPartners />
            </PermissionRoute>
          }
        />
        
      </Route>

      <Route path="*" element={<Navigate to="/app" replace />} />
    </Routes>
  </AppProvider>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/qr" element={<QRMenu />} />

            {/* Legal Pages */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route
              path="/terms-and-conditions"
              element={<TermsAndConditions />}
            />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />

            <Route
              path="/app/*"
              element={
                <Protected>
                  <Shell />
                </Protected>
              }
            />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
